/**
 * Publication Service
 */

import { httpClient } from '../http-client';

const API_BASE_URL = '/api/v1';

export interface PublicationRevision {
  id: string;
  version: number;
  themeSlug?: string;
  themeConfig?: Record<string, unknown>;
  content?: string;
  status?: string;
  starNumber?: number;
  packageUrl?: string | null;
  publishedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PublicationDetail {
  id: string;
  learningTreeId: string;
  title: string;
  description?: string;
  status: string;
  includedNodeIds: string[];
  latestRevision?: PublicationRevision;
  revisions?: PublicationRevision[];
  createdAt?: string;
  updatedAt?: string;
}

export interface PublicationListItem {
  id: string;
  title: string;
  status: string;
  learningTreeId: string;
  latestRevision?: PublicationRevision;
  createdAt?: string;
  updatedAt?: string;
}

export const PublicationService = {
  /**
   */
  async createPublication(params: {
    learningTreeId: string;
    title: string;
    description?: string;
    includedNodeIds: string[];
    themeSlug?: string;
    content?: string;
  }): Promise<{
    id: string;
    status: string;
    latestRevision?: {
      id: string;
      version: number;
    };
  }> {
    try {
      const url = `${API_BASE_URL}/publications`;
      const response = await httpClient.post<{
        id: string;
        status: string;
        latestRevision?: { id: string; version: number };
      }>(url, params);
      return response.data;
    } catch (error) {
      console.error('Error creating publication:', error);
      throw error;
    }
  },

  /**
   */
  async getPublications(params?: {
    status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
    page?: number;
    limit?: number;
  }): Promise<{
    items: PublicationListItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
  }> {
    try {
      const url = `${API_BASE_URL}/publications`;
      const response = await httpClient.get<{
        items: PublicationListItem[];
        pagination: { page: number; limit: number; total: number };
      }>(url, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching publications:', error);
      throw error;
    }
  },

  /**
   */
  async getPublication(id: string): Promise<PublicationDetail> {
    try {
      const url = `${API_BASE_URL}/publications/${id}`;
      const response = await httpClient.get<PublicationDetail>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching publication:', error);
      throw error;
    }
  },

  /**
   */
  async updateRevision(
    publicationId: string,
    revisionId: string,
    data: {
      themeSlug?: string;
      themeConfig?: Record<string, unknown>;
      content?: string;
    }
  ): Promise<PublicationRevision> {
    try {
      const url = `${API_BASE_URL}/publications/${publicationId}/revisions/${revisionId}`;
      const response = await httpClient.put<PublicationRevision>(url, data);
      return response.data;
    } catch (error) {
      console.error('Error updating revision:', error);
      throw error;
    }
  },

  /**
   */
  async publishRevision(
    publicationId: string,
    revisionId: string
  ): Promise<{
    starNumber: number;
    packageUrl: string | null;
    publishedAt: string;
  }> {
    try {
      const url = `${API_BASE_URL}/publications/${publicationId}/revisions/${revisionId}/publish`;
      const response = await httpClient.post<{
        starNumber: number;
        packageUrl: string | null;
        publishedAt: string;
      }>(url);
      return response.data;
    } catch (error) {
      console.error('Error publishing revision:', error);
      throw error;
    }
  },

  /**
   */
  async createRevision(
    publicationId: string,
    data: {
      includedNodeIds: string[];
      themeSlug?: string;
      basedOnRevisionId?: string;
    }
  ): Promise<PublicationRevision> {
    try {
      const url = `${API_BASE_URL}/publications/${publicationId}/revisions`;
      const response = await httpClient.post<PublicationRevision>(url, data);
      return response.data;
    } catch (error) {
      console.error('Error creating revision:', error);
      throw error;
    }
  },
};
